import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useVisibleNavigationItems } from '../../navigation/hooks/useVisibleNavigationItems';
import { useSMLSettingsTabs } from '../../../settings/tabs/useSMLSettingsTabs';
import { PalettePage, palettePages } from './palettePages';
import { commandPaletteMessages as messages } from './commandPaletteMessages';

export interface PalettePageMatch {
  page: PalettePage;
  /** "Charts · Artists" */
  title: string;
}

interface NamedPage extends PalettePageMatch {
  /** Every name in lower case: the title, the own name, the old names. */
  names: string[];
}

/**
 * The rows of "Pages and actions" for the search text: with no text the menu items, else every
 * page, tab and Settings tab whose name or old name holds the text.
 */
export function usePalettePageMatches(query: string): PalettePageMatch[] {
  const intl = useIntl();
  const items = useVisibleNavigationItems();
  const settingsTabs = useSMLSettingsTabs();

  const named = useMemo(
    () =>
      palettePages(items, settingsTabs).map((page): NamedPage => {
        const own = intl.formatMessage(page.title);
        const title =
          page.parent === undefined
            ? own
            : intl.formatMessage(messages.tabTitle, { page: intl.formatMessage(page.parent), tab: own });
        const names = [title, own, ...page.aliases.map((alias) => intl.formatMessage(alias))].map((name) =>
          name.toLowerCase()
        );
        return { page, title, names };
      }),
    [intl, items, settingsTabs]
  );

  return useMemo(() => {
    const text = query.trim().toLowerCase();
    const matches =
      text.length === 0
        ? named.filter((row) => row.page.inMenu)
        : named.filter((row) => row.names.some((name) => name.includes(text)));
    return matches.map(({ page, title }) => ({ page, title }));
  }, [named, query]);
}
